import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart, ProductImageType, ProductType } from '../../context/CartContext';
import { getProductById, getProductsByType, arkansasTrademarkedSportNames } from '../../services/productService';
import { ColorOptions, BeadColorOptions } from '../../services/ColorOptions';
import '../../ProductBase.css';
import './index.css';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [customizationValues, setCustomizationValues] = useState<{[itemName: string]: string}>({});
  const [trademarkError, setTrademarkError] = useState<string>('');

  const handleCustomizationChange = (itemName: string, value: string) => {
    if (itemName === 'Name' && arkansasTrademarkedSportNames.some(name => name.toLowerCase() === value.toLowerCase())) {
      setTrademarkError('Arkansas trademarked sport names are not allowed.');
      return;
    }
    
    // Clear trademark error if user changes to a valid name
    if (itemName === 'Name' && trademarkError) {
      setTrademarkError('');
    }
    
    setCustomizationValues(prev => ({
      ...prev,
      [itemName]: value
    }));
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    // If product is customizable, validate that all customization fields have been filled
    if (product.is_customizeable && product.customization_items) {
      const hasEmptyCustomizations = product.customization_items.some(item => {
        const userValue = customizationValues[item.name];
        // Check if user hasn't entered anything or entered only whitespace
        return !userValue || userValue.trim() === '';
      });
      
      if (hasEmptyCustomizations) {
        alert('Please customize all required fields before adding to cart.');
        return;
      }
      
      // Check for trademarked names in customization values
      const hasTrademarkedName = product.customization_items.some(item => {
        const userValue = customizationValues[item.name];
        return item.name === 'Name' && userValue && 
               arkansasTrademarkedSportNames.some(name => name.toLowerCase() === userValue.toLowerCase());
      });
      
      if (hasTrademarkedName) {
        setTrademarkError('Arkansas trademarked sport names are not allowed.');
        return;
      }
    }
    
    // Create updated customization items with user-entered values
    const updatedCustomizationItems = product.customization_items?.map(item => {
      let customText = customizationValues[item.name] || item.customText;
      
      // If this is a color field and we have a hex code, convert it to friendly name
      if ((item.name === 'BeadColor' || item.name === 'KeychainColor') && customText) {
        const colorEntry = Object.entries(BeadColorOptions).find(([, hex]) => hex === customText);
        if (colorEntry) {
          customText = colorEntry[0].replace(/_/g, ' ');
        }
      }
      else if (item.name.indexOf('Color') !== -1 && customText) {
        const colorEntry = Object.entries(ColorOptions).find(([, hex]) => hex === customText);
        if (colorEntry) {
          customText = colorEntry[0].replace(/_/g, ' ');
        }
      }
      
      return {
        ...item,
        customText
      };
    }) || [];

    addToCart({ 
      ...product, 
      quantity: 1, 
      is_customizeable: product.is_customizeable ?? false, 
      customization_items: updatedCustomizationItems 
    });
    
    // Clear the customization values and trademark error after adding to cart
    setCustomizationValues({});
    setTrademarkError('');
  };

  // Prevent caching of product detail pages - only run once per product ID
  useEffect(() => {
    // Only add cache-busting if we don't already have a timestamp
    const url = new URL(window.location.href);
    if (!url.searchParams.has('_t')) {
      const timestamp = Date.now();
      url.searchParams.set('_t', timestamp.toString());
      window.history.replaceState({}, '', url.toString());
    }
  }, [id]); // Only run when product ID changes

  const product = id ? getProductById(id) : undefined;

  if (!product) {
    return <div>Product not found</div>;
  }

  // Get other products in the same category (excluding current product)
  const categoryProducts = getProductsByType(product.product_type).filter(p => p.id !== product.id).slice(0, 6);

  // Function to navigate to category shop page
  const handleCategoryNavigation = () => {
    switch (product.product_type) {
      case ProductType.SNAPCHARM:
        navigate('/shop/snapcharms');
        break;
      case ProductType.SPORTS:
        navigate('/shop/sports');
        break;
      case ProductType.LOCALS:
        navigate('/shop/locals');
        break;
      case ProductType.StoreFront:
        navigate('/shop/storefront');
        break;
      default:
        navigate('/shop');
    }
  };

  return (
    <div className="product-detail-container">
      <div className="product-content">
        <div className="product-info">
          <div className="product-summary">
            <div className="product-image-container">
              <img src={product.images.find(img => img.image_type === ProductImageType.DETAIL)?.image_path || product.images[0].image_path} alt={product.name} className="product-detail-image" />
            </div>
            <div className="product-header">
              <h1 className="product-title">{product.name}</h1>
              <div className="product-price">${product.price.toFixed(2)}</div>
            </div>
          </div>
          <p className="product-description">{product.description}<br/>Dimensions: {product.height_inches} x {product.width_inches} x {product.depth_inches} inches</p>
          {product.is_customizeable && product.customization_items && (
            <div className="customization-section">
              <h3>Customize It !</h3>

              {product.customization_items.map((item) => (
                <div key={item.name} className="customization-item">
                  <label htmlFor={`custom-${item.name}`}>{item.name}:</label>
                  {item.name.indexOf('Color') !== -1 ? (
                    <div className="color-selection">
                      <div className="color-swatches">
                        {(item.name === 'BeadColor' || item.name === 'KeychainColor' ? Object.entries(BeadColorOptions) : Object.entries(ColorOptions)).map(([friendlyName, hexCode]) => (
                          <div
                            key={hexCode}
                            className={`color-swatch ${customizationValues[item.name] === hexCode ? 'selected' : ''}`}
                            style={{ backgroundColor: `#${hexCode}` }}
                            title={friendlyName.replace(/_/g, ' ')}
                            onClick={() => handleCustomizationChange(item.name, hexCode)}
                          />
                        ))}
                      </div>
                      {customizationValues[item.name] && (
                        <div className="selected-color-info">
                          Selected: {
                            item.name === 'BeadColor' || item.name === 'KeychainColor'
                              ? Object.entries(BeadColorOptions).find(([, hex]) => hex === customizationValues[item.name])?.[0].replace(/_/g, ' ')
                              : Object.entries(ColorOptions).find(([, hex]) => hex === customizationValues[item.name])?.[0].replace(/_/g, ' ')
                          }
                        </div>
                      )}
                    </div>
                  ) : (
                     <input 
                      id={`custom-${item.name}`}
                      type="text"
                      maxLength={item.max_length} 
                      size={item.max_length}
                      placeholder={item.customText}
                      value={customizationValues[item.name] || ''}
                      onChange={(e) => handleCustomizationChange(item.name, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          
          {trademarkError && (
            <div className="trademark-error">
              <p>{trademarkError}</p>
            </div>
          )}
          {product.product_type !== ProductType.StoreFront && (
            <section className="trademark-policy">
              <small>Protected tradenames or names, image, and likeness (NIL) rights can not be used!</small><br/><a href="/legal">Read our Custom Orders & Personalization Policy</a>
            </section>
          )}
          <div className="cart-controls">
            <button className="add-to-cart-button" onClick={handleAddToCart}>
              Add to Cart
            </button>
            <button className="add-to-cart-button" onClick={() => navigate('/shop')}>Continue Shopping</button>
          </div>
        </div>
      </div>
      
      {/* Category Preview Section */}
      {categoryProducts.length > 0 && (
        <div className="category-preview-section">
          <div className="category-preview-header">
            <button className="view-all-button" onClick={handleCategoryNavigation}>
              See More
            </button>
          </div>
          <div className="category-preview-grid">
            {categoryProducts.map((categoryProduct) => (
              <div
                key={categoryProduct.id}
                className="category-preview-item"
                onClick={() => navigate(`/product/${categoryProduct.id}`)}
              >
                <img
                  src={categoryProduct.images.find(img => img.image_type === ProductImageType.SMALL)?.image_path || categoryProduct.images[0].image_path}
                  alt={categoryProduct.name}
                  className="category-preview-image"
                />
                <div className="category-preview-info">
                  <span className="category-preview-name">{categoryProduct.name}</span>
                  <span className="category-preview-price">${categoryProduct.price.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
